import React, { useCallback, useEffect, useState } from "react";
import { MapPin, Check, X, Loader2, RefreshCw, ExternalLink, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, LoadingBlock } from "../../components/app/ui";
import { adminApi, errorMessage, formatDate } from "../../lib/api";

const TABS = ["pending", "approved", "rejected", "all"];

export default function AddressChanges() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setItems(null);
    adminApi.addressChanges(status)
      .then((r) => { setItems(r.requests || []); setPendingCount(r.pending_count || 0); })
      .catch((e) => { toast.error(errorMessage(e)); setItems([]); });
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (it) => {
    setBusyId(it.request_id);
    try { await adminApi.approveAddressChange(it.request_id); toast.success("Address change approved"); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };
  const reject = async (it) => {
    const reason = window.prompt("Reason for rejecting this address change (shared with the restaurant):", "");
    if (reason === null) return;
    setBusyId(it.request_id);
    try { await adminApi.rejectAddressChange(it.request_id, reason); toast.success("Request rejected"); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusyId(null); }
  };

  const mapsLink = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
  const movedKm = (it) => {
    if (it.moved_km != null) return it.moved_km;
    if (it.old_lat == null || it.new_lat == null) return null;
    const R = 6371, toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(it.new_lat - it.old_lat), dLng = toRad(it.new_lng - it.old_lng);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(it.old_lat)) * Math.cos(toRad(it.new_lat)) * Math.sin(dLng / 2) ** 2;
    return Math.round(R * 2 * Math.asin(Math.sqrt(a)) * 10) / 10;
  };

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Restaurant address changes" description="Review and approve restaurant address change requests. Approving swaps the restaurant's live address and location." />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((t) => (
            <button key={t} onClick={() => setStatus(t)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${status === t ? "bg-brand text-white" : "bg-surface-100 text-ink-soft hover:bg-surface-200"}`} data-testid={`addrch-tab-${t}`}>
              {t}{t === "pending" && pendingCount ? ` (${pendingCount})` : ""}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto" data-testid="addrch-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {items === null ? <LoadingBlock /> : items.length === 0 ? (
        <EmptyState icon={MapPin} title={`No ${status === "all" ? "" : status} requests`} text="Restaurant address change requests will show here." />
      ) : (
        <div className="space-y-3" data-testid="addrch-list">
          {items.map((it) => (
            <div key={it.request_id} className="rounded-2xl border border-surface-300 bg-white p-4" data-testid={`addrch-card-${it.request_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-ink">{it.restaurant_name || it.restaurant_id}</p>
                  <p className="mt-0.5 text-[11px] text-ink-muted">{formatDate(it.created_at)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold capitalize ${it.status === "pending" ? "bg-amber-100 text-amber-800" : it.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`addrch-status-${it.request_id}`}>{it.status}</span>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-surface-200 bg-surface-50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">Current</p>
                  <p className="mt-1 text-sm text-ink-soft">{it.old_address || "—"}</p>
                  {it.old_lat != null && <a href={mapsLink(it.old_lat, it.old_lng)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-brand underline">Map <ExternalLink className="h-3 w-3" /></a>}
                </div>
                <div className="rounded-xl border border-brand-200 bg-brand-50/50 p-3">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-brand">Requested</p>
                  <p className="mt-1 text-sm font-medium text-ink">{it.new_address}</p>
                  {it.new_lat != null && <a href={mapsLink(it.new_lat, it.new_lng)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-brand underline">Map <ExternalLink className="h-3 w-3" /></a>}
                </div>
              </div>

              {it.reason && <p className="mt-3 text-sm text-ink-soft"><span className="font-semibold text-ink">Reason:</span> {it.reason}</p>}
              {(() => { const km = movedKm(it); return km != null ? (
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-soft" data-testid={`addrch-distance-${it.request_id}`}>
                  <MapPin className="h-3.5 w-3.5" /> Pin moves ~{km} km
                  {km >= 3 && (it.rekyc_triggered ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800" data-testid={`addrch-rekyc-${it.request_id}`}>Re-verification requested</span> : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700">Approving will request re-KYC</span>)}
                </p>
              ) : null; })()}
              {it.proof_url && <a href={it.proof_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-brand underline" data-testid={`addrch-proof-${it.request_id}`}><FileText className="h-4 w-4" /> View proof document</a>}
              {it.status === "rejected" && it.reject_reason && <p className="mt-2 text-xs text-red-600">Rejected: {it.reject_reason}</p>}
              {it.reviewed_by_name && it.status !== "pending" && <p className="mt-1 text-[11px] text-ink-muted">By {it.reviewed_by_name} · {formatDate(it.reviewed_at)}</p>}

              {it.status === "pending" && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => approve(it)} disabled={busyId === it.request_id} className="h-9 rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`addrch-approve-${it.request_id}`}>
                    {busyId === it.request_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1.5 h-4 w-4" /> Approve &amp; apply</>}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(it)} disabled={busyId === it.request_id} className="h-9 rounded-full border-red-300 text-red-600 hover:bg-red-50" data-testid={`addrch-reject-${it.request_id}`}>
                    <X className="mr-1.5 h-4 w-4" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
